import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Dialog } from "@/components/ui";
import { SocialNetworkProfileServices, ZillowLenderImportSession } from "@/services/bedrock";
import { getErrorMessage } from "@/utils";
import copy from "copy-to-clipboard";
import { CheckCircle2, ClipboardCopy, ExternalLink, Loader2, Terminal } from "lucide-react";
import { toast } from "sonner";
interface Props {
  open: boolean;
  userId: string | number;
  profileUrl?: string;
  onOpenChange: (open: boolean) => void;
  onComplete?: () => void;
}

export function ZillowReviewsImportDialog({ open, userId, profileUrl, onOpenChange, onComplete }: Props) {
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);
  const [isStarting, setIsStarting] = useState(false);
  const [session, setSession] = useState<ZillowLenderImportSession | null>(null);
  const [clipboardCopied, setClipboardCopied] = useState(false);
  const [zillowOpened, setZillowOpened] = useState(false);
  const snippetRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) {
      setIsStarting(false);
      setSession(null);
      setClipboardCopied(false);
      setZillowOpened(false);
    }
  }, [open]);

  const copyBootstrapSnippet = useCallback((snippet: string) => {
    const copied = copy(snippet, { format: "text/plain" });
    if (copied) {
      setClipboardCopied(true);
      toast.success("Import script copied to clipboard");
      return;
    }

    const textarea = snippetRef.current;
    if (textarea) {
      textarea.focus();
      textarea.select();
      const selected = document.execCommand("copy");
      if (selected) {
        setClipboardCopied(true);
        toast.success("Import script copied to clipboard");
        return;
      }
    }

    toast.error("Could not copy automatically", {
      description: "Select the script below and copy it manually (Cmd+C or Ctrl+C).",
    });
  }, []);

  const handleStartImport = useCallback(async () => {
    setIsStarting(true);
    try {
      const importSession = await SocialNetworkProfileServices.createZillowLenderImportSession(userId);
      setSession(importSession);
    } catch (error: unknown) {
      console.error(error);
      toast.error("Unable to start Zillow import", {
        description: getErrorMessage(error),
      });
    } finally {
      setIsStarting(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!open || session || isStarting) return;
    if (profileUrl == null) {
      toast.error("Unable to start Zillow import", {
        description: "A Zillow lender profile URL is required.",
      });
      close();
      return;
    }
    handleStartImport().catch(console.error);
  }, [open, session, isStarting, profileUrl, handleStartImport, close]);

  const handleDone = useCallback(() => {
    onComplete?.();
    close();
  }, [close, onComplete]);

  const instructionSteps = useMemo(
    () => [
      {
        id: "open-profile",
        done: zillowOpened,
        icon: ExternalLink,
        title: "Open your lender profile",
        description: "Open your Zillow lender profile in a new browser tab.",
        action: (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              window.open(profileUrl, "_blank", "noopener,noreferrer");
              setZillowOpened(true);
            }}
          >
            <ExternalLink size={14} />
            {zillowOpened ? "Open again" : "Open"}
          </Button>
        ),
      },
      {
        id: "copy-script",
        done: clipboardCopied,
        icon: ClipboardCopy,
        title: "Copy the import script",
        description: "Click Copy script, then paste it into the Zillow tab console.",
        action: session ? (
          <div className="space-y-2">
            <Button variant="outline" size="sm" onClick={() => copyBootstrapSnippet(session.bootstrapSnippet)}>
              <ClipboardCopy size={14} />
              {clipboardCopied ? "Copy again" : "Copy script"}
            </Button>
            <textarea
              ref={snippetRef}
              readOnly
              value={session.bootstrapSnippet}
              rows={4}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-700"
              onFocus={e => e.target.select()}
            />
          </div>
        ) : undefined,
      },
      {
        id: "paste-console",
        icon: Terminal,
        title: "Paste into the browser console",
        description:
          "On the Zillow tab, press F12 or right-click → Inspect → Console, paste the script, and press Enter.",
      },
      {
        id: "wait-for-import",
        icon: CheckCircle2,
        title: "Wait for import to finish",
        description:
          "The script collects all reviews and sends them to SocialCoach. When it finishes, click Done here.",
      },
    ],
    [zillowOpened, profileUrl, clipboardCopied, session, copyBootstrapSnippet]
  );

  return (
    <Dialog open={open} onOpenChange={nextOpen => !nextOpen && close()} size="lg">
      <Dialog.Header>
        <Dialog.Title>Finish import on Zillow</Dialog.Title>
        <Dialog.Description>
          Follow these steps to import all reviews from your Zillow lender profile.
        </Dialog.Description>
      </Dialog.Header>
      <Dialog.Content className="flex flex-col gap-4">
        {isStarting && !session ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="animate-spin" size={16} />
            Preparing import...
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
            {instructionSteps.map(({ id, done, icon: Icon, title, description, action }) => (
              <div key={id} className="flex gap-3">
                <div
                  className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                    done ? "bg-emerald-100 text-emerald-700" : "bg-white text-slate-500 border border-slate-200"
                  }`}
                >
                  <Icon size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm text-slate-900">{title}</p>
                  <p className="text-sm text-slate-600 mt-0.5">{description}</p>
                  {action && <div className="mt-2">{action}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
        {session?.expiresAt && (
          <p className="text-xs text-muted-foreground">
            This import session expires at {new Date(session.expiresAt).toLocaleTimeString()}.
          </p>
        )}
      </Dialog.Content>
      <Dialog.Footer>
        <Button variant="ghost" onClick={close}>
          Close
        </Button>
        <Button onClick={handleDone} disabled={!session}>
          Done
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
}
